import * as React from "react";
import { useFormContext } from 'react-hook-form';
import Select, { createFilter, ValueType } from 'react-select';
import { ICareProvider } from "../../models/ICareProvider";
import { SelectStyles, MenuList } from "soprasteria-ui-library";
import "./CareProviderDropdown.scss";

interface IOption {
    value: string;
    label: string;
    selected?: boolean;
}

interface ICareProivderDropdown {
    careProviders: ICareProvider[] | undefined;
    defaultValue?: string;
    labelCol?: number;
    inputCol?: number;
    name: string;
    isLoading: boolean;
    onValueChange?: (newValue: string) => void;
    required?: boolean;
    label?: string;
    useFixedListItemHeight?: boolean;
    disabled?: boolean;
    isClearable?: boolean;
    placeholder?: string;
    showSelectionArea?: boolean;
    showClinicId?: boolean;
    showOrgNum?: boolean;
    resetValue?: boolean;
    clearValueIfNoInitalValue?: boolean;
}

export const CareProviderDropdown: React.FunctionComponent<ICareProivderDropdown> = ({ careProviders, defaultValue, labelCol, inputCol, name, isLoading, onValueChange, required, label = "Mottagning", useFixedListItemHeight, disabled, isClearable, placeholder = "Välj mottagning", showSelectionArea, showClinicId, showOrgNum, resetValue, clearValueIfNoInitalValue }) => {

    const { register, setValue, errors, unregister } = useFormContext();
    const [selectedValue, setSelectedValue] = React.useState<IOption>();
    const [careProvidersOptions, setCareProvidersOptions] = React.useState<IOption[]>()
    var selectRef = React.useRef<any>();

    React.useEffect(() => {
        document.getElementById("clear-form")?.removeEventListener("click", handleResetValue);
        if (!disabled && careProviders && (isClearable || careProviders.length > 1)) {
            document.getElementById("clear-form")?.addEventListener("click", handleResetValue);
        }

        return () => {
            document.getElementById("clear-form")?.removeEventListener("click", handleResetValue);
        }
    }, [careProviders])

    React.useEffect(() => {
        handleResetValue();
    }, [resetValue])

    const handleResetValue = () => {
        if (selectRef?.current) {
            selectRef.current.select.clearValue();
            setValue(name, undefined)
            setSelectedValue(undefined)
        }
    }

    const setName = (careProvider: ICareProvider) => {
        if (showSelectionArea) {
            return `${careProvider.name} - ${careProvider.selectionAreas?.map((selectionArea) => { return selectionArea.description })}`;
        }
        if (showClinicId) {
            return careProvider.clinicNumber ? `${careProvider.clinicNumber} - ${careProvider.name}` : careProvider.name
        }
        if (showOrgNum) {
            return `${careProvider.orgNum} - ${careProvider.name}`
        }
        return careProvider.name;
    }

    React.useEffect(() => {
        register({ name: name }, { required: required });
        if (careProviders) {
            const options = careProviders.map<IOption>((careProvider) => {
                return {
                    value: careProvider.careProviderId?.toString(),
                    label: setName(careProvider),
                    selected: defaultValue !== undefined && careProvider.careProviderId?.toString() === defaultValue
                }
            });

            const initalValue = options.find((option) => { return option.selected === true });
            if (initalValue) {
                setValue(name, initalValue.value);
                setSelectedValue(initalValue);
            }
            else {
                if (clearValueIfNoInitalValue) {
                    selectRef.current.select.clearValue();
                }
                setValue(name, undefined);
                setSelectedValue(undefined);
            }

            setCareProvidersOptions(options);
        }

        return () => {
            unregister(name);
        }

    }, [careProviders, defaultValue, required]);

    const onChange = (selectedOption: any | null) => {
        if (selectedOption) {
            setValue(name, selectedOption.value);
            setSelectedValue(selectedOption);
        }
        else {
            setValue(name, undefined);
            setSelectedValue(undefined)
        }
        if (onValueChange) {
            onValueChange(selectedOption?.value ?? "");
        }
    }

    return (
        <div className="form-group row">
            <label className={`col-${labelCol ?? 4} col-form-label`}>{label}:{required ? "*" : ""}</label>
            <div className={`col-${inputCol ?? 8}`}>
                <Select
                    ref={selectRef}
                    isLoading={isLoading}
                    loadingMessage={() => "Laddar..."}
                    noOptionsMessage={() => { return "Det finns inga valbara mottagningar" }}
                    placeholder={placeholder}
                    filterOption={createFilter({
                        ignoreAccents: false,
                        matchFrom: 'any',
                        stringify: option => `${option.label}`
                    })}
                    options={careProvidersOptions}
                    isOptionSelected={(option: IOption) => option.selected === true}
                    value={selectedValue}
                    onChange={(selectedOption: any) => onChange(selectedOption)}
                    components={useFixedListItemHeight ? { MenuList } : undefined}
                    styles={useFixedListItemHeight ? SelectStyles : undefined}
                    isDisabled={disabled === true || (disabled === undefined && defaultValue !== undefined && careProvidersOptions?.length === 1)}
                    isClearable={isClearable !== undefined ? isClearable : true}
                    classNamePrefix="custom-care-provider-dropdown-styling"
                />

                <span className="text-danger">{errors ? [name] && (errors[name] as any)?.type === "required" && "Välj en mottagning" : ""}</span>
            </div>
        </div>
    )
}